import type { SupabaseClient } from "@supabase/supabase-js";

import type { ServiceExceptionCode, ServiceProvenance, ServiceStyleToken } from "./service-week-approval";
import { isServiceInstant, isServiceLocalDate, isServiceTimeZone, isServiceWeekStart } from "../lib/service-week";

export type ScheduleCoverageFillState = "open" | "assigned" | "cancelled";
export type ScheduleCoverageOpenFillMode = "approval_required" | "instant_claim";
export type ScheduleCoverageDisposition = "pending" | "included" | "rejected";
export type ScheduleCoverageDutyLineage =
  | "assigned"
  | "replaced_before_service"
  | "handoff_during_service"
  | "declined_before_duty";

export interface ScheduleCoverageSummary {
  publishedSeatCount: number;
  coveredSeatCount: number;
  uncoveredSeatCount: number;
  openSeatCount: number;
  exceptionCount: number;
}

export interface ScheduleCoverageDutyInterval {
  intervalOrdinal: number;
  personLabel: string | null;
  recordedStart: string | null;
  startProvenance: ServiceProvenance;
  recordedEnd: string | null;
  endProvenance: ServiceProvenance;
  lineage: ScheduleCoverageDutyLineage;
  exceptionCodes: ServiceExceptionCode[];
}

interface ScheduleCoverageRowCommon {
  rowOrdinal: number;
  serviceDay: string;
  serviceLabel: string;
  serviceLabelKind: "post" | "route";
  exceptionCodes: ServiceExceptionCode[];
  dutyIntervals: ScheduleCoverageDutyInterval[];
}

export type ScheduleCoverageScheduledRow = ScheduleCoverageRowCommon & {
  kind: "scheduled_seat";
  seatLocator: string;
  templateLabel: string;
  templateStyleToken: ServiceStyleToken;
  scheduledStart: string;
  scheduledEnd: string;
  fillState: ScheduleCoverageFillState;
  openFillMode: ScheduleCoverageOpenFillMode | null;
  disposition: null;
};

export type ScheduleCoverageUnscheduledRow = ScheduleCoverageRowCommon & {
  kind: "unscheduled";
  seatLocator: null;
  templateLabel: null;
  templateStyleToken: null;
  scheduledStart: null;
  scheduledEnd: null;
  fillState: null;
  openFillMode: null;
  disposition: ScheduleCoverageDisposition;
};

export type ScheduleCoverageRow = ScheduleCoverageScheduledRow | ScheduleCoverageUnscheduledRow;

export type ScheduleCoverageExceptions =
  | {
      status: "ok";
      basis: "live_sources";
      weekEnded: boolean;
      timeZone: string;
      weekStart: string;
      weekEndExclusive: string;
      summary: ScheduleCoverageSummary;
      rows: ScheduleCoverageRow[];
    }
  | { status: "empty"; reason: "no_scheduled_service" }
  | {
      status: "unavailable";
      reason: "target_unavailable" | "source_incomplete" | "too_large" | "unavailable";
    };

export type ScheduleCoverageKey = {
  accountId: string;
  clientId: string;
  siteId: string;
  weekStart: string;
};

const EXCEPTION_CODES = [
  "cancelled_after_publish",
  "uncovered",
  "partial",
  "missing_start",
  "missing_end",
  "late_start",
  "early_end",
  "officer_entered",
  "supervisor_set",
  "unscheduled",
] as const satisfies readonly ServiceExceptionCode[];

const STYLE_TOKENS = [
  "slate",
  "blue",
  "indigo",
  "teal",
  "green",
  "amber",
  "orange",
  "rose",
  "red",
  "violet",
  "cyan",
  "stone",
] as const satisfies readonly ServiceStyleToken[];

const UNAVAILABLE_REASONS = ["target_unavailable", "source_incomplete", "too_large", "unavailable"] as const;

const unavailable = (): Extract<ScheduleCoverageExceptions, { status: "unavailable" }> => ({
  status: "unavailable",
  reason: "unavailable",
});

export const scheduleCoverageCopy = {
  loading: "Loading coverage...",
  unavailable: "Coverage is unavailable right now. Try again.",
  targetsEmpty: "No active client sites are available for coverage.",
  empty: "No service was scheduled for this site and week.",
  runningWeek: "This week is still running. These facts are not approved.",
  endedWeekLead: "This week has ended. Approve it in Schedule, Timesheets.",
  openFillApproval: "This seat does not fill itself. Someone must approve a request.",
  openFillInstant: "Instant claim",
  openFillApprovalLabel: "Approval required",
} as const;

export const scheduleCoverageExceptionCopy: Record<ServiceExceptionCode, string> = {
  cancelled_after_publish: "Cancelled after publish",
  uncovered: "Uncovered",
  partial: "Partial service",
  missing_start: "Missing start",
  missing_end: "Missing end",
  late_start: "Late start",
  early_end: "Early end",
  officer_entered: "Officer-entered time",
  supervisor_set: "Supervisor-set time",
  unscheduled: "Unscheduled service",
};

function record(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function exact(value: Record<string, unknown>, keys: readonly string[]): boolean {
  return Object.keys(value).length === keys.length && keys.every((key) => Object.hasOwn(value, key));
}

function safeLabel(value: unknown, max = 120): value is string {
  if (typeof value !== "string" || value.length === 0 || value.length > max) return false;
  if (/[\u0000-\u001f\u007f-\u009f]/.test(value)) return false;
  if (/[\u202a-\u202e\u2066-\u2069]/.test(value)) return false;
  if (/\s{2,}/.test(value)) return false;
  if (/^https?:\/\//i.test(value) || value.includes("@")) return false;
  return true;
}

function parseExceptionCodes(value: unknown): ServiceExceptionCode[] | null {
  if (!Array.isArray(value)) return null;
  const codes: ServiceExceptionCode[] = [];
  for (const item of value) {
    if (typeof item !== "string" || !(EXCEPTION_CODES as readonly string[]).includes(item)) return null;
    codes.push(item as ServiceExceptionCode);
  }
  return codes;
}

function parseDutyInterval(value: unknown): ScheduleCoverageDutyInterval | null {
  if (!record(value) || !exact(value, ["intervalOrdinal", "personLabel", "recordedStart", "startProvenance", "recordedEnd", "endProvenance", "lineage", "exceptionCodes"])) {
    return null;
  }
  if (typeof value.intervalOrdinal !== "number" || !Number.isInteger(value.intervalOrdinal) || value.intervalOrdinal < 1) return null;
  if (value.personLabel !== null && !safeLabel(value.personLabel, 40)) return null;
  const startProvenance = value.startProvenance;
  const endProvenance = value.endProvenance;
  if (startProvenance !== "device_verified" && startProvenance !== "officer_entered" && startProvenance !== "supervisor_set" && startProvenance !== "missing") {
    return null;
  }
  if (endProvenance !== "device_verified" && endProvenance !== "officer_entered" && endProvenance !== "supervisor_set" && endProvenance !== "missing") {
    return null;
  }
  if (value.recordedStart !== null && !isServiceInstant(value.recordedStart)) return null;
  if (value.recordedEnd !== null && !isServiceInstant(value.recordedEnd)) return null;
  const lineage = value.lineage;
  if (lineage !== "assigned" && lineage !== "replaced_before_service" && lineage !== "handoff_during_service" && lineage !== "declined_before_duty") {
    return null;
  }
  const exceptionCodes = parseExceptionCodes(value.exceptionCodes);
  if (exceptionCodes === null) return null;
  return {
    intervalOrdinal: value.intervalOrdinal,
    personLabel: value.personLabel as string | null,
    recordedStart: value.recordedStart as string | null,
    startProvenance: startProvenance,
    recordedEnd: value.recordedEnd as string | null,
    endProvenance: endProvenance,
    lineage,
    exceptionCodes,
  };
}

function parseRow(value: unknown): ScheduleCoverageRow | null {
  if (!record(value)) return null;
  if (value.kind === "unscheduled") {
    const keys = ["rowOrdinal", "kind", "serviceDay", "serviceLabel", "serviceLabelKind", "templateLabel", "templateStyleToken", "scheduledStart", "scheduledEnd", "seatLocator", "fillState", "openFillMode", "disposition", "exceptionCodes", "dutyIntervals"] as const;
    if (!exact(value, keys)) return null;
    if (typeof value.rowOrdinal !== "number" || !Number.isInteger(value.rowOrdinal) || value.rowOrdinal < 1) return null;
    if (!isServiceLocalDate(value.serviceDay)) return null;
    if (!safeLabel(value.serviceLabel) || value.serviceLabelKind !== "route") return null;
    if (value.templateLabel !== null || value.templateStyleToken !== null) return null;
    if (value.scheduledStart !== null || value.scheduledEnd !== null || value.seatLocator !== null) return null;
    if (value.fillState !== null || value.openFillMode !== null) return null;
    if (value.disposition !== "pending" && value.disposition !== "included" && value.disposition !== "rejected") return null;
    const exceptionCodes = parseExceptionCodes(value.exceptionCodes);
    if (exceptionCodes === null || !exceptionCodes.includes("unscheduled")) return null;
    if (!Array.isArray(value.dutyIntervals) || value.dutyIntervals.length !== 0) return null;
    return {
      rowOrdinal: value.rowOrdinal,
      kind: "unscheduled",
      serviceDay: value.serviceDay,
      serviceLabel: value.serviceLabel,
      serviceLabelKind: "route",
      templateLabel: null,
      templateStyleToken: null,
      scheduledStart: null,
      scheduledEnd: null,
      seatLocator: null,
      fillState: null,
      openFillMode: null,
      disposition: value.disposition,
      exceptionCodes,
      dutyIntervals: [],
    };
  }
  if (value.kind !== "scheduled_seat") return null;
  const keys = ["rowOrdinal", "kind", "seatLocator", "serviceDay", "serviceLabel", "serviceLabelKind", "templateLabel", "templateStyleToken", "scheduledStart", "scheduledEnd", "fillState", "openFillMode", "disposition", "exceptionCodes", "dutyIntervals"] as const;
  if (!exact(value, keys)) return null;
  if (typeof value.rowOrdinal !== "number" || !Number.isInteger(value.rowOrdinal) || value.rowOrdinal < 1) return null;
  if (typeof value.seatLocator !== "string" || value.seatLocator.length < 8 || value.seatLocator.length > 24) return null;
  if (!isServiceLocalDate(value.serviceDay)) return null;
  if (!safeLabel(value.serviceLabel) || (value.serviceLabelKind !== "post" && value.serviceLabelKind !== "route")) return null;
  if (!safeLabel(value.templateLabel)) return null;
  if (typeof value.templateStyleToken !== "string" || !(STYLE_TOKENS as readonly string[]).includes(value.templateStyleToken)) return null;
  if (!isServiceInstant(value.scheduledStart) || !isServiceInstant(value.scheduledEnd)) return null;
  if (value.fillState !== "open" && value.fillState !== "assigned" && value.fillState !== "cancelled") return null;
  if (value.openFillMode !== null && value.openFillMode !== "approval_required" && value.openFillMode !== "instant_claim") return null;
  if (value.disposition !== null) return null;
  const exceptionCodes = parseExceptionCodes(value.exceptionCodes);
  if (exceptionCodes === null) return null;
  if (!Array.isArray(value.dutyIntervals)) return null;
  const dutyIntervals: ScheduleCoverageDutyInterval[] = [];
  for (const interval of value.dutyIntervals) {
    const parsed = parseDutyInterval(interval);
    if (parsed === null) return null;
    dutyIntervals.push(parsed);
  }
  return {
    rowOrdinal: value.rowOrdinal,
    kind: "scheduled_seat",
    seatLocator: value.seatLocator,
    serviceDay: value.serviceDay,
    serviceLabel: value.serviceLabel,
    serviceLabelKind: value.serviceLabelKind,
    templateLabel: value.templateLabel,
    templateStyleToken: value.templateStyleToken as ServiceStyleToken,
    scheduledStart: value.scheduledStart,
    scheduledEnd: value.scheduledEnd,
    fillState: value.fillState,
    openFillMode: value.openFillMode as ScheduleCoverageOpenFillMode | null,
    disposition: null,
    exceptionCodes,
    dutyIntervals,
  };
}

export function parseScheduleCoverageExceptions(value: unknown): ScheduleCoverageExceptions {
  if (!record(value) || typeof value.status !== "string") return unavailable();
  if (value.status === "empty") {
    if (!exact(value, ["status", "reason"]) || value.reason !== "no_scheduled_service") return unavailable();
    return value as Extract<ScheduleCoverageExceptions, { status: "empty" }>;
  }
  if (value.status === "unavailable") {
    if (!exact(value, ["status", "reason"]) || !(UNAVAILABLE_REASONS as readonly string[]).includes(String(value.reason))) {
      return unavailable();
    }
    return value as Extract<ScheduleCoverageExceptions, { status: "unavailable" }>;
  }
  if (value.status !== "ok") return unavailable();
  const keys = ["status", "basis", "weekEnded", "timeZone", "weekStart", "weekEndExclusive", "summary", "rows"] as const;
  if (!exact(value, keys) || value.basis !== "live_sources" || typeof value.weekEnded !== "boolean") return unavailable();
  if (!isServiceTimeZone(value.timeZone) || !isServiceWeekStart(value.weekStart) || !isServiceWeekStart(value.weekEndExclusive)) return unavailable();
  if (!record(value.summary) || !exact(value.summary, ["publishedSeatCount", "coveredSeatCount", "uncoveredSeatCount", "openSeatCount", "exceptionCount"])) {
    return unavailable();
  }
  for (const key of ["publishedSeatCount", "coveredSeatCount", "uncoveredSeatCount", "openSeatCount", "exceptionCount"] as const) {
    const n = value.summary[key];
    if (typeof n !== "number" || !Number.isInteger(n) || n < 0) return unavailable();
  }
  if (!Array.isArray(value.rows)) return unavailable();
  const rows: ScheduleCoverageRow[] = [];
  for (const row of value.rows) {
    const parsed = parseRow(row);
    if (parsed === null) return unavailable();
    rows.push(parsed);
  }
  for (let i = 0; i < rows.length; i += 1) {
    if (rows[i]?.rowOrdinal !== i + 1) return unavailable();
  }
  return {
    status: "ok",
    basis: "live_sources",
    weekEnded: value.weekEnded,
    timeZone: value.timeZone,
    weekStart: value.weekStart,
    weekEndExclusive: value.weekEndExclusive,
    summary: {
      publishedSeatCount: value.summary.publishedSeatCount as number,
      coveredSeatCount: value.summary.coveredSeatCount as number,
      uncoveredSeatCount: value.summary.uncoveredSeatCount as number,
      openSeatCount: value.summary.openSeatCount as number,
      exceptionCount: value.summary.exceptionCount as number,
    },
    rows,
  };
}

export async function fetchScheduleCoverageExceptions(
  client: SupabaseClient,
  key: ScheduleCoverageKey,
): Promise<ScheduleCoverageExceptions> {
  try {
    const { data, error } = await client.rpc("management_schedule_coverage_exceptions", {
      p_account_id: key.accountId,
      p_client_id: key.clientId,
      p_site_id: key.siteId,
      p_week_start: key.weekStart,
    });
    if (error) return unavailable();
    return parseScheduleCoverageExceptions(data);
  } catch {
    return unavailable();
  }
}
